import React, { useEffect, useRef, useState } from "react";

const CELL_COUNT = 6;

function isRecoveryCodeValue(value) {
  if (!value) return false;
  if (value.length > CELL_COUNT) return true;
  return /[^0-9]/.test(value);
}

function digitsFromValue(value) {
  const digits = Array(CELL_COUNT).fill("");
  const numeric = String(value || "").replace(/\D/g, "").slice(0, CELL_COUNT);
  for (let i = 0; i < numeric.length; i += 1) {
    digits[i] = numeric[i];
  }
  return digits;
}

function MfaOtpInput({ value, onChange, disabled, onEnter, inputId = "mfa-otp-0" }) {
  const inputRefs = useRef([]);
  const [recoveryMode, setRecoveryMode] = useState(() => isRecoveryCodeValue(value));

  useEffect(() => {
    if (!value) {
      setRecoveryMode(false);
      return;
    }
    if (isRecoveryCodeValue(value)) {
      setRecoveryMode(true);
    }
  }, [value]);

  useEffect(() => {
    if (!recoveryMode && !disabled) {
      inputRefs.current[0]?.focus();
    }
  }, [recoveryMode, disabled]);

  const digits = digitsFromValue(recoveryMode ? "" : value);

  const commitDigits = (nextDigits) => {
    onChange(nextDigits.join("").replace(/\D/g, "").slice(0, CELL_COUNT));
  };

  const focusCell = (index) => {
    const el = inputRefs.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleCellChange = (index, raw) => {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      const next = [...digits];
      next[index] = "";
      commitDigits(next);
      return;
    }

    if (cleaned.length > 1) {
      const next = [...digits];
      let cursor = index;
      for (const ch of cleaned) {
        if (cursor >= CELL_COUNT) break;
        next[cursor] = ch;
        cursor += 1;
      }
      commitDigits(next);
      focusCell(Math.min(cursor, CELL_COUNT - 1));
      return;
    }

    const next = [...digits];
    next[index] = cleaned;
    commitDigits(next);
    if (index < CELL_COUNT - 1) {
      focusCell(index + 1);
    }
  };

  const handleCellKeyDown = (index, event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onEnter?.();
      return;
    }

    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = "";
      commitDigits(next);
      focusCell(index - 1);
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusCell(index - 1);
      return;
    }

    if (event.key === "ArrowRight" && index < CELL_COUNT - 1) {
      event.preventDefault();
      focusCell(index + 1);
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").trim();
    if (!pasted) return;
    event.preventDefault();

    if (isRecoveryCodeValue(pasted)) {
      setRecoveryMode(true);
      onChange(pasted);
      return;
    }

    const numeric = pasted.replace(/\D/g, "").slice(0, CELL_COUNT);
    onChange(numeric);
    focusCell(Math.min(numeric.length, CELL_COUNT - 1));
  };

  const switchToRecovery = () => {
    setRecoveryMode(true);
    onChange("");
  };

  const switchToOtp = () => {
    setRecoveryMode(false);
    onChange("");
  };

  if (recoveryMode) {
    return (
      <div className="mfa-otp mfa-otp--recovery">
        <label className="mfa-otp__recovery-label" htmlFor="mfa-recovery-code">
          Recovery code
        </label>
        <input
          id="mfa-recovery-code"
          type="text"
          className="mfa-otp__recovery-input"
          value={value}
          disabled={disabled}
          autoComplete="one-time-code"
          autoFocus
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onEnter?.();
          }}
          placeholder="Enter recovery code"
        />
        <button
          type="button"
          className="mfa-otp__mode-link"
          onClick={switchToOtp}
          disabled={disabled}
        >
          Use authenticator code instead
        </button>
      </div>
    );
  }

  const renderCell = (index) => (
    <input
      key={index}
      ref={(el) => {
        inputRefs.current[index] = el;
      }}
      id={index === 0 ? inputId : undefined}
      type="text"
      inputMode="numeric"
      autoComplete={index === 0 ? "one-time-code" : "off"}
      className="mfa-otp__cell"
      value={digits[index]}
      placeholder="-"
      disabled={disabled}
      maxLength={6}
      aria-label={`Digit ${index + 1} of ${CELL_COUNT}`}
      onChange={(e) => handleCellChange(index, e.target.value)}
      onKeyDown={(e) => handleCellKeyDown(index, e)}
      onPaste={handlePaste}
      onFocus={(e) => e.target.select()}
    />
  );

  return (
    <div className="mfa-otp">
      <div
        className="mfa-otp__cells"
        role="group"
        aria-label="6-digit authentication code"
      >
        {[0, 1, 2].map(renderCell)}
        <span className="mfa-otp__sep" aria-hidden="true">–</span>
        {[3, 4, 5].map(renderCell)}
      </div>
      <button
        type="button"
        className="mfa-otp__mode-link"
        onClick={switchToRecovery}
        disabled={disabled}
      >
        Use a recovery code instead
      </button>
    </div>
  );
}

export default MfaOtpInput;
